import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import SearchPanel from "@widgets/SearchPanel";
import FreeForm from "@widgets/FreeForm";
import SnGrid from "@shared/components/Grid/SnGrid";
import SnResizable from "@shared/components/SnResizable";
import { showDialog } from "@widgets/CommonDialog";
import { fnGetDataSet, fnSave } from "@shared/api/restApi";
import { getInitPopUp, getInitPopUpData } from "@shared/utils/CommonUtils";
import { global } from "@shared/libs/Global";
import { menuInfo } from "@shared/type/CommonType";
import { SnButton } from "@shared/components/SnButton";

interface Props {
  modalRef?: any;
  Menuprops?: menuInfo;
  initialParams?: {
    cd_pjt?: string;
    cd_unit?: string;
    cd_item?: string;
    nm_pjt?: string;
    nm_st_pjt?: string;
  };
}

const SN_PMS_PJT_REG_004_POP_002 = forwardRef(
  ({ modalRef, Menuprops, initialParams }: Props, ref) => {
    const { userInfo } = global();
    const searchRef = useRef<any>(null);
    const freeFormRef = useRef<any>(null);
    const gridRef = useRef<any>(null);
    const popupRootRef = useRef<HTMLDivElement>(null);
    const [popupParentEl, setPopupParentEl] = useState<HTMLElement | null>(
      null
    );

    const [formData, setFormData] = useState<any>({});
    const [searchControls, setSearchControls] = useState<any[]>([]);
    const [freeform1Controls, setFreeform1Controls] = useState<any[]>([]);
    const [gridColumns, setGridColumns] = useState<any[]>([]);
    const [gridData, setGridData] = useState<any[]>([]);

    const defaultPanelSize = {
      panel1: "60%",
      panel2: "40%",
    };
    const [panelSizes, setPanelSizes] = useState<{
      [key: string]: string | number;
    }>(defaultPanelSize);

    useImperativeHandle(ref, () => ({
      checkValidity: async () => {
        const changes = gridRef.current?.getChange?.();
        const hasChange = changes?.modifiedRowcount > 0;
        return {
          flag: true,
          hasGridChanges: !!hasChange,
        };
      },
      getSelectedValue: async () => {
        const ok = await onConfirm();
        if (ok) return "ok";
        // onConfirm 내부에서 메시지를 이미 처리하므로 CommonModal 기본 메시지 방지
        return { excelUpload: false };
      },
      onConfirm,
      onCancel,
    }));

    useEffect(() => {
      setPopupParentEl(popupRootRef.current);
    }, []);

    useEffect(() => {
      const fetchInit = async () => {
        try {
          const paramSeed = {
            ...(initialParams || {}),
            ...(modalRef?.params || {}),
            nm_pjt:
              (modalRef?.params?.nm_pjt ?? initialParams?.nm_pjt ?? "") || "",
            nm_st_pjt:
              (modalRef?.params?.nm_st_pjt ?? initialParams?.nm_st_pjt ?? "") ||
              "",
          };
          const rtn: any = await getInitPopUpData(
            "SN_PMS_PJT_REG_004_POP_002",
            {
              cd_menu: Menuprops?.cd_menu || modalRef?.cd_menu,
              cd_firm: userInfo?.cd_firm,
              cd_item: paramSeed?.cd_item,
            }
          );
          const mergedFormData = {
            ...(rtn.formData || {}),
            ...paramSeed,
          };
          setFormData(mergedFormData);
          setSearchControls(rtn.form?.search || rtn.formControlData);
          setFreeform1Controls(rtn.form?.freeform1);
          setGridColumns(rtn.grid?.grid1);
          setGridData(rtn.gridData || []);
          if (initialParams?.cd_pjt || initialParams?.cd_unit) {
            setTimeout(() => onSearch(), 0);
          }
        } catch (error) {}
      };
      fetchInit();
    }, [Menuprops, modalRef, userInfo, initialParams]);

    const onGrid1BtnsClick = async (id: string) => {
      if (id === "addRow") {
        onAddRow();
      }
      if (id === "deleteRow") {
        onDeleteRow();
      }
      if (id === "modifyRow") {
        onModifyRow();
      }
    };

    const syncGridDataFromApi = () => {
      const rows: any[] = [];
      if (gridRef.current?.api?.forEachNode) {
        gridRef.current.api.forEachNode((node: any) => {
          if (node?.data) rows.push({ ...node.data });
        });
      } else {
        rows.push(...(gridRef.current?.getRowData?.() ?? []));
      }
      setGridData(rows);
    };

    const buildDefaultRow = (baseRow: any = {}) => {
      const paramSeed = {
        ...(initialParams || {}),
        ...(modalRef?.params || {}),
      };

      const currentRows = gridRef.current?.getRowData?.() ?? gridData ?? [];
      const firstRow = currentRows[0] || {};
      const maxLine =
        currentRows.reduce(
          (acc: number, r: any) => Math.max(acc, Number(r?.no_line) || 0),
          0
        ) || 0;
      const nextLine = maxLine + 1;
      const parentNoLine = Number(
        baseRow?.no_line_p ??
          formData?.no_line_p ??
          paramSeed?.no_line_p ??
          firstRow?.no_line_p ??
          0
      );
      const parentNoLevel = Number(
        baseRow?.no_level_p ??
          formData?.no_level_p ??
          paramSeed?.no_level_p ??
          -1
      );

      const defaults = {
        state: "insert",
        cd_firm: userInfo?.cd_firm,
        cd_pjt: paramSeed.cd_pjt,
        cd_unit: paramSeed.cd_unit,
        // 차수추가에서는 라인의 모품목(cd_item)을 항상 선택 품목으로 고정
        cd_item: paramSeed.cd_item,
        no_hst: paramSeed.no_hst ?? formData?.no_hst ?? firstRow?.no_hst ?? "",
        no_line:
          baseRow.no_line != null && baseRow.no_line !== ""
            ? baseRow.no_line
            : nextLine,
        no_line_p:
          baseRow.no_line_p != null && baseRow.no_line_p !== ""
            ? baseRow.no_line_p
            : parentNoLine,
        no_level:
          baseRow.no_level != null && baseRow.no_level !== ""
            ? baseRow.no_level
            : parentNoLevel + 1,
        no_seq:
          baseRow.no_seq != null && baseRow.no_seq !== ""
            ? baseRow.no_seq
            : nextLine,
        qt_just:
          baseRow.qt_just != null && baseRow.qt_just !== ""
            ? baseRow.qt_just
            : 1,
        rt_loss:
          baseRow.rt_loss != null && baseRow.rt_loss !== ""
            ? baseRow.rt_loss
            : 0,
        qt_need:
          baseRow.qt_need != null && baseRow.qt_need !== ""
            ? baseRow.qt_need
            : 1,
        fg_eco: baseRow.fg_eco ?? "I1",
        no_rev: firstRow?.no_rev,
        cd_cb: firstRow?.cd_cb,
        no_cbs: firstRow?.no_cbs,
        cd_dummy01: firstRow?.cd_dummy01,
        cd_dummy02: firstRow?.cd_dummy02,
        cd_dummy03: firstRow?.cd_dummy03,
        cd_dummy04: firstRow?.cd_dummy04,
        cd_dummy05: firstRow?.cd_dummy05,
        nm_dummy01: firstRow?.nm_dummy01,
        nm_dummy02: firstRow?.nm_dummy02,
        nm_dummy03: firstRow?.nm_dummy03,
        nm_dummy04: firstRow?.nm_dummy04,
        nm_dummy05: firstRow?.nm_dummy05,
      };

      const merged = { ...defaults, ...baseRow };
      // 멀티 품목추가 시 baseRow(cd_item)가 자재코드로 들어와도 모품목은 고정
      merged.cd_item = paramSeed.cd_item;
      return merged;
    };

    const onAddRow = async () => {
      // PRD 패턴과 동일: addRow({}) + onBeforeAddRow에서 초기값 주입
      gridRef.current?.addRow({});
      syncGridDataFromApi();
    };

    const onDeleteRow = async () => {
      const api = gridRef.current?.api;
      if (!api) return;
      let selectedNodes = api.getSelectedNodes?.() || [];
      if (!selectedNodes.length) {
        const focused = api.getFocusedCell?.();
        if (focused && focused.rowIndex != null) {
          const node = api.getDisplayedRowAtIndex?.(focused.rowIndex);
          if (node) selectedNodes = [node];
        }
      }
      if (!selectedNodes.length) {
        await showDialog("alter", "선택된 행이 없습니다.");
        return;
      }

      const toRemove: any[] = [];
      const changed: string[] = [];

      selectedNodes.forEach((node: any) => {
        const row = node?.data || {};
        if (row.state === "insert") {
          toRemove.push(row);
        } else {
          const updated = { ...row, fg_eco: "D" };
          node.setData?.(updated);
          changed.push(`row:${node.rowIndex ?? ""} fg_eco -> D`);
        }
      });

      if (toRemove.length) api.applyTransaction?.({ remove: toRemove });
      syncGridDataFromApi();

      if (changed.length) {
        //await showDialog("alter", changed.join("\n"));
      }
    };

    const onModifyRow = async () => {
      const api = gridRef.current?.api;
      if (!api) return;
      let selectedNodes = api.getSelectedNodes?.() || [];
      if (!selectedNodes.length) {
        const focused = api.getFocusedCell?.();
        if (focused && focused.rowIndex != null) {
          const node = api.getDisplayedRowAtIndex?.(focused.rowIndex);
          if (node) selectedNodes = [node];
        }
      }
      if (!selectedNodes.length) {
        await showDialog("alter", "선택된 행이 없습니다.");
        return;
      }

      // 신규(state === "insert")가 포함되어 있으면 경고 후 중단
      const hasInsert = selectedNodes.some(
        (node: any) => (node.data || {}).state === "insert"
      );
      if (hasInsert) {
        await showDialog("alter", "ECO구분을 확인하세요.");
        return;
      }

      const changed: string[] = [];
      selectedNodes.forEach((node: any) => {
        const row = { ...(node.data || {}) };
        row.fg_eco = "U";
        node.setData?.(row);
        changed.push(`row:${node.rowIndex ?? ""} fg_eco -> U`);
      });

      // 정미수량(qt_just) 칼럼으로 포커스 이동
      const firstNode = selectedNodes[0];
      const rowIndex = firstNode?.rowIndex ?? 0;
      api.setFocusedCell?.(rowIndex, "qt_just");
      api.ensureIndexVisible?.(rowIndex);
      syncGridDataFromApi();

      if (changed.length) {
        // await showDialog("alter", changed.join("\n"));
      }
    };
    const onSearch = async () => {
      try {
        debugger;
        const paramSeed = {
          ...(initialParams || {}),
          ...(modalRef?.params || {}),
          nm_pjt:
            (modalRef?.params?.nm_pjt ?? initialParams?.nm_pjt ?? "") || "",
          nm_st_pjt:
            (modalRef?.params?.nm_st_pjt ?? initialParams?.nm_st_pjt ?? "") ||
            "",
        };
        const baseParams = {
          cd_pjt: paramSeed.cd_pjt,
          cd_unit: paramSeed.cd_unit,
          cd_item: paramSeed.cd_item,
          nm_pjt: paramSeed.nm_pjt,
          nm_st_pjt: paramSeed.nm_st_pjt,
          nm_item_old: paramSeed.nm_item_old,
        };
        const formParams = searchRef.current?.getFormdata?.() ?? {};
        const safeInitialParams = Object.fromEntries(
          Object.entries(paramSeed).filter(
            ([, v]) => v !== undefined && v !== null
          )
        );
        const params = {
          id: "sn_pms_pjt_reg_004_pop_002_01",
          params: {
            ...baseParams,
            ...formParams,
            cd_firm: userInfo?.cd_firm,
          },
        };
        const data: any = await fnGetDataSet(params);
        // data[0]: freeform, data[1]: grid
        if (data?.[0]?.[0]) {
          setFormData((prev: any) => {
            const mergedForm = {
              ...prev,
              ...data[0][0], // 조회값 우선 반영
              ...safeInitialParams, // 동일 키는 초기 파라미터(정의된 값만) 최종 유지
            };
            // FreeForm/Search 내부 상태도 즉시 반영
            freeFormRef.current?.setFormdata?.(mergedForm);
            searchRef.current?.setFormdata?.(mergedForm);
            return mergedForm;
          });
        }
        setGridData(data?.[1] || []);
      } catch (error) {}
    };

    const toBig = (v: any) => {
      const n = Number(v ?? 0);
      return isNaN(n) ? 0 : n;
    };

    const focusDcRevision = () => {
      freeFormRef.current?.setFocus?.("dc_revision");
      freeFormRef.current?.focus?.("dc_revision");
      const el =
        (document.querySelector(
          '[name="dc_revision"]'
        ) as HTMLInputElement | null) ||
        (document.querySelector("#dc_revision") as HTMLInputElement | null);
      el?.focus?.();
      el?.select?.();
    };

    const onConfirm = async () => {
      gridRef.current?.stopEditing?.(); // 편집 중인 셀 커밋
      const freeformValues = freeFormRef.current?.getFormdata?.() ?? {};
      const searchValues = searchRef.current?.getFormdata?.() ?? {};
      // gridRef.getRowData가 최신값을 반영하지 않는 경우가 있어 node 순회로 보강
      const rows: any[] = [];
      if (gridRef.current?.api?.forEachNode) {
        gridRef.current.api.forEachNode((node: any) => {
          if (node?.data) rows.push({ ...node.data });
        });
      } else {
        rows.push(...(gridRef.current?.getRowData?.() ?? gridData ?? []));
      }
      if (!rows.length) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      const pick = (obj: any, keys: string[]) => {
        for (const k of keys) {
          const v = obj?.[k];
          if (v !== undefined && v !== null && v !== "") return v;
        }
        return undefined;
      };

      const mergedForm = {
        ...(formData || {}),
        ...(searchValues || {}),
        ...(freeformValues || {}),
      };

      const cd_pjt = mergedForm.cd_pjt;
      if (!cd_pjt) {
        await showDialog("alter", "프로젝트를 입력해주세요.");
        return false;
      }

      const fg_revision_val = pick(mergedForm, [
        "fg_revision",
        "fg_revi",
        "fg_rev",
        "fg_re",
      ]);

      const dc_revision_val = pick(mergedForm, ["dc_revision", "dc_revi"]);
      if (!String(dc_revision_val ?? "").trim()) {
        await showDialog("alter", "변경내용을 입력해주세요.");
        focusDcRevision();
        return false;
      }

      const lines =
        rows
          .map((r: any) => {
            return {
              cd_firm: userInfo?.cd_firm,
              cd_pjt: cd_pjt,
              cd_unit: r.cd_unit || mergedForm.cd_unit,
              // 차수추가 저장은 모든 라인이 동일 모품목 기준으로 저장
              cd_item: mergedForm.cd_item,
              no_hst: toBig(r.no_hst || mergedForm.no_hst || 0),
              no_id_ebomh: null,
              no_line: toBig(r.no_line),
              no_line_p: toBig(r.no_line_p || 0),
              no_level: toBig(r.no_level || 0),
              no_seq: toBig(r.no_seq || 0),
              cd_matl: r.cd_matl,
              no_rev: r.no_rev || mergedForm.no_rev || "0",
              qt_just: toBig(r.qt_just || 0),
              rt_loss: toBig(r.rt_loss || 0),
              qt_need: toBig(r.qt_need || 0),
              dc_rmk: r.dc_rmk,
              fg_eco: r.fg_eco,
              cd_cb: r.cd_cb,
              no_cbs: r.no_cbs,
              dc_revision: dc_revision_val,
              cd_user_reg: userInfo?.cd_user,
            };
          }) || [];

      const payload = {
        pms_pjt_eboml: {
          insert: lines,
          update: [],
          delete: [],
        },
      };

      try {
        const ok = await fnSave("SN_PMS_PJT_REG_004_SAVE_01", payload);
        if (!ok) {
          return false;
        }
        return true;
      } catch (e: any) {
        await showDialog("error", e?.message || "저장 중 오류가 발생했습니다.");
        return false;
      }
    };

    const onCancel = () => {
      modalRef?.current?.handleConfirm?.(false, null);
    };

    const onResizeStopPanel = (seq: number, newSize: number) => {
      const key = seq === 1 ? "panel1" : "panel2";
      setPanelSizes((prev) => ({ ...prev, [key]: newSize }));
    };

    return (
      <div className="h-full flex flex-col" ref={popupRootRef}>
        <div className="mb-1 shrink-0">
          <SearchPanel
            ref={searchRef}
            data={formData}
            controls={searchControls}
          />
        </div>
        <div className="flex-1 h-full min-h-0 space-y-1.5">
          <SnResizable
            direction={"height"}
            seq={1}
            cd_menu={Menuprops?.cd_menu || "SN_PMS_PJT_REG_004_POP_002"}
            defaultSize={defaultPanelSize}
            size={panelSizes.panel1}
            onResizeStop={onResizeStopPanel}
            enable={{ bottom: true }}
            minHeight={250}
          >
            <div className="h-full overflow-hidden">
              <FreeForm
                ref={freeFormRef}
                data={formData}
                controls={freeform1Controls}
              />
            </div>
          </SnResizable>

          <SnResizable
            direction={"height"}
            seq={2}
            cd_menu={Menuprops?.cd_menu || "SN_PMS_PJT_REG_004_POP_002"}
            defaultSize={defaultPanelSize}
            size={panelSizes.panel2}
            onResizeStop={onResizeStopPanel}
            enable={{ bottom: true }}
            minHeight={150}
          >
            <div className="h-full overflow-hidden">
              <SnGrid
                ref={gridRef}
                menuInfo={modalRef}
                popupParent={popupParentEl || undefined}
                enableAddRow={true}
                columnConfig={gridColumns}
                rowData={gridData}
                isEditable={true}
                title="BOM 정보 "
                header={
                  <div className="space-x-2">
                    <SnButton
                      text="추가"
                      onClick={() => onGrid1BtnsClick("addRow")}
                    />
                    <SnButton
                      text="삭제"
                      onClick={() => onGrid1BtnsClick("deleteRow")}
                    />
                    <SnButton
                      text="수정"
                      onClick={() => onGrid1BtnsClick("modifyRow")}
                    />
                  </div>
                }
                onBeforeAddRow={(row: any) => {
                  const merged = buildDefaultRow(row || {});
                  Object.assign(row, merged);
                  return true;
                }}
              />
            </div>
          </SnResizable>
        </div>
      </div>
    );
  }
);

export default SN_PMS_PJT_REG_004_POP_002;
